import { Icon16ArrowRightOutline } from '@vkontakte/icons';
import { ReactComponent as ArrowLeftIcon } from 'img/icons/vkui/arrow_left_outline_24.svg';
import { ReactComponent as ArrowRightIcon } from 'img/icons/vkui/arrow_right_outline_24.svg';
import React, { type ReactElement, memo } from 'react';
import { useSelector } from 'react-redux';
import { isRebranding } from 'reactApp/appHelpers/featuresHelpers';
import type { WelcomeTrialParams } from 'reactApp/appHelpers/featuresHelpers/features.types';
import { onboardingStepsEnabled } from 'reactApp/appHelpers/featuresHelpers/features/onboardingSteps';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { CreateBlock } from 'reactApp/ui/WelcomePromo/WelcomeNewPromo/CreateBlock';
import { PhoneAppBlock } from 'reactApp/ui/WelcomePromo/WelcomeNewPromo/PhoneAppBlock';
import { TrialBlock } from 'reactApp/ui/WelcomePromo/WelcomeNewPromo/TrialBlock/TrialBlock';
import { UploaderBlock } from 'reactApp/ui/WelcomePromo/WelcomeNewPromo/UploaderBlock';
import { useWelcomePromoLogic } from 'reactApp/ui/WelcomePromo/WelcomePromo.hooks';
import { FreeMode, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import styles from './WelcomeNewPromo.css';

interface Props {
    showSubscriptions: boolean;
    space?: string;
    trialData?: WelcomeTrialParams | null;

    onSubscriptionsClick(): void;
}

export const WelcomeNewPromoComponent = memo(({ showSubscriptions, space, onSubscriptionsClick, trialData }: Props): ReactElement => {
    const { isLoaded: isSingleQuotaLoaded } = useSelector(UserQuotaSelectors.getLoadingState);

    return (
        <div className={styles.root}>
            <div className={styles.textBlock}>
                <div className={styles.header}>Добро пожаловать в Облако!</div>
                <div className={styles.description}>
                    {isSingleQuotaLoaded && (
                        <>
                            Теперь у вас есть <span className={styles.quota}>{space}</span>.
                        </>
                    )}{' '}
                    Загружайте фото, делитесь файлами
                    <br /> и работайте вместе с другими пользователями.{' '}
                    {showSubscriptions && (
                        <span className={styles.tariffs} onClick={onSubscriptionsClick}>
                            Тарифы <Icon16ArrowRightOutline display="inline" />
                        </span>
                    )}
                </div>
            </div>
            <div className={styles.actionBlock}>
                <div className={styles.slider}>
                    <Swiper
                        slidesPerView="auto"
                        spaceBetween={16}
                        navigation={{
                            nextEl: `.${styles.arrowNext}`,
                            prevEl: `.${styles.arrowPrev}`,
                            disabledClass: styles.arrowDisabled,
                        }}
                        modules={[Navigation, FreeMode]}
                    >
                        {trialData && !onboardingStepsEnabled && (
                            <SwiperSlide key="trial" className={styles.slide}>
                                <TrialBlock data={trialData} />
                            </SwiperSlide>
                        )}
                        {isRebranding ? (
                            <>
                                <SwiperSlide key="1" className={styles.slide}>
                                    <PhoneAppBlock noPhoneTheme={onboardingStepsEnabled} />
                                </SwiperSlide>
                                <SwiperSlide key="2" className={styles.slide}>
                                    <UploaderBlock />
                                </SwiperSlide>
                                <SwiperSlide key="3" className={styles.slide}>
                                    <CreateBlock />
                                </SwiperSlide>
                            </>
                        ) : (
                            <>
                                <SwiperSlide key="1" className={styles.slide}>
                                    <UploaderBlock />
                                </SwiperSlide>
                                <SwiperSlide key="2" className={styles.slide}>
                                    <CreateBlock />
                                </SwiperSlide>
                                <SwiperSlide key="3" className={styles.slide}>
                                    <PhoneAppBlock />
                                </SwiperSlide>
                            </>
                        )}
                    </Swiper>
                    <div className={styles.arrowPrev}>
                        <ArrowLeftIcon />
                    </div>
                    <div className={styles.arrowNext}>
                        <ArrowRightIcon />
                    </div>
                </div>
            </div>
        </div>
    );
});

WelcomeNewPromoComponent.displayName = 'WelcomeNewPromoComponent';

export const WelcomeNewPromo = memo((): ReactElement => {
    const userTotalSpace = useSelector(UserQuotaSelectors.getTotal);

    const { trialData, handleSubscriptionsClick, showSubscriptions } = useWelcomePromoLogic({ forNewUser: true });

    return (
        <WelcomeNewPromoComponent
            showSubscriptions={showSubscriptions}
            onSubscriptionsClick={handleSubscriptionsClick}
            space={userTotalSpace?.value}
            trialData={trialData}
        />
    );
});

WelcomeNewPromo.displayName = 'WelcomeNewPromo';
